import {
  useVideoPlayer2,
  useVideoPlayerWatcher,
} from "./use-video-player-2-setup-start";

function formatTime(time: number) {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  // Because video duration could be sent with milliseconds i.e 132.41, we need to ceil the seconds so displayed duration will be 2:13 instead of 2:12
  const seconds = Math.ceil(time % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function useVideoPlayerDuration() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("durationChanged", "duration");
  useVideoPlayerWatcher("currentTimeChanged", "currentTime");
  useVideoPlayerWatcher("bufferedTimeChanged");

  const { duration = 0, currentTime = 0 } = player;

  return {
    duration,
    currentTime,
    setCurrentTime: player.setCurrentTime,
    formatTime,
    get formattedDuration() {
      return formatTime(duration);
    },
    get formattedCurrentTime() {
      return formatTime(currentTime);
    },
    get bufferedTime() {
      return player.bufferedTime;
    },
    get formattedBufferedTime() {
      return formatTime(player.bufferedTime);
    },
  };
}

export function useVideoPlayer2Fullscreen() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("fullscreenChanged", "isFullscreen");

  const { isFullscreen = false } = player;

  return { isFullscreen, toggleFullscreen: player.toggleFullscreen };
}

export function useVideoPlayer2PictureInPicture() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("pictureInPictureChanged", "isPictureInPicture");

  const { isPictureInPicture = false } = player;

  return {
    isPictureInPicture,
    togglePictureInPicture: player.togglePictureInPicture,
  };
}

export function useVideoPlayer2Volume() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("volumeChanged");

  const { volume = 0 } = player;

  return {
    volume,
    setVolume: player.setVolume,
    isMuted: player.isMuted,
    toggleMute: player.toggleMute,
    get volumeLevel() {
      if (player.isMuted || volume === 0) return "muted";
      if (volume <= 0.33) return "low";
      if (volume <= 0.66) return "medium";
      return "high";
    },
  };
}

export function useVideoPlayer2Qualities() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("qualityLevels.setup", "qualityLevels");
  useVideoPlayerWatcher("qualityLevels.changed");
  useVideoPlayerWatcher("qualityLevels.selected");

  const { qualityLevels = [], currentQualityLevel } = player;

  return {
    qualityLevels,
    currentQualityLevel,
    setQuality: player.setCurrentQualityLevel,
  };
}

export function useVideoPlayer2Loading() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("loading");

  return player.isLoading;
}

export function useVideoPlayer2ControlsVisible() {
  const player = useVideoPlayer2();
  useVideoPlayerWatcher("controlsVisible");

  return {
    controlsVisible: player.controlsVisible || false,
  };
}
